import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu, X, Instagram, Youtube } from 'lucide-react';
import { useCart } from '../App';
import { useSettings } from '../App';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SocialIcon = ({ platform }) => {
  switch (platform.toLowerCase()) {
    case 'instagram': return <Instagram size={18} />;
    case 'youtube': return <Youtube size={18} />;
    case 'tiktok': return <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg>;
    case 'pinterest': return <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0a12 12 0 0 0-4.37 23.17c-.1-.94-.2-2.38.04-3.4l1.46-6.17s-.37-.75-.37-1.86c0-1.74 1-3.04 2.26-3.04 1.07 0 1.58.8 1.58 1.76 0 1.07-.68 2.67-1.03 4.15-.3 1.24.62 2.25 1.84 2.25 2.21 0 3.9-2.33 3.9-5.7 0-2.98-2.14-5.06-5.2-5.06-3.54 0-5.62 2.66-5.62 5.4 0 1.07.41 2.22.93 2.85.1.12.12.23.08.35l-.35 1.4c-.05.22-.18.27-.4.16-1.49-.7-2.42-2.87-2.42-4.62 0-3.76 2.73-7.21 7.88-7.21 4.14 0 7.35 2.95 7.35 6.88 0 4.1-2.59 7.41-6.18 7.41-1.21 0-2.34-.63-2.73-1.37l-.74 2.83c-.27 1.03-1 2.33-1.49 3.12A12 12 0 1 0 12 0z"/></svg>;
    default: return null;
  }
};

const Navbar = ({ hideMenu = false }) => {
  const { cart } = useCart();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [socialMedia, setSocialMedia] = useState([]);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const fetchSocial = async () => {
      try {
        const res = await axios.get(`${API}/social`);
        setSocialMedia(res.data.filter(s => s.is_active && s.show_in_header));
      } catch (e) {}
    };
    fetchSocial();

    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    { label: 'Problem', href: '#problem' },
    { label: 'Rozwiązanie', href: '#solution' },
    { label: 'Produkty', href: '#produkty' },
    { label: 'Opinie', href: '#opinie' },
    { label: 'FAQ', href: '#faq' },
    { label: 'Kontakt', href: '/kontakt', isPage: true }
  ];

  const scrollToSection = (href) => {
    setIsMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 navbar-blur transition-all duration-300 ${isScrolled ? 'bg-[#050505]/90 border-b border-white/5' : 'bg-transparent'}`} data-testid="navbar">
      <div className="container-custom flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2" data-testid="logo">
          {settings.site_logo_type === 'image' && settings.site_logo ? (
            <img src={settings.site_logo} alt={settings.site_name} className="h-8" />
          ) : (
            <span className="text-xl md:text-2xl font-bold">
              <span className="text-white">{settings.site_name?.split(/(?=[A-Z])/).slice(0, -1).join('')}</span>
              <span className="text-[#10b981]">{settings.site_name?.split(/(?=[A-Z])/).slice(-1)[0] || settings.site_name}.</span>
            </span>
          )}
        </Link>

        {/* Desktop Menu */}
        {!hideMenu && (
          <div className="hidden md:flex items-center gap-8">
            {menuItems.map((item) => (
              item.isPage ? (
                <Link key={item.label} to={item.href} className="text-[#9CA3AF] hover:text-[#34d399] transition-colors hover-glow" data-testid={`nav-${item.label.toLowerCase()}`}>
                  {item.label}
                </Link>
              ) : (
                <button key={item.label} onClick={() => scrollToSection(item.href)} className="text-[#9CA3AF] hover:text-[#34d399] transition-colors hover-glow" data-testid={`nav-${item.label.toLowerCase()}`}>
                  {item.label}
                </button>
              )
            ))}
          </div>
        )}

        {/* Right Side */}
        <div className="flex items-center gap-4">
          {/* Cart */}
          <button onClick={() => navigate('/checkout')} className="relative p-2 text-[#9CA3AF] hover:text-[#10b981] transition-colors" data-testid="cart-button">
            <ShoppingCart size={22} />
            {cart.length > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#10b981] text-white text-xs font-bold rounded-full flex items-center justify-center">
                {cart.length}
              </span>
            )}
          </button>

          {/* CTA Button - Desktop */}
          {!hideMenu ? (
            <button onClick={() => scrollToSection('#produkty')} className="hidden md:flex btn-primary py-2 px-5 text-sm" data-testid="nav-cta">
              Kup teraz
            </button>
          ) : (
            <Link to="/#produkty" className="hidden md:flex btn-primary py-2 px-5 text-sm" data-testid="nav-cta">
              Kup teraz
            </Link>
          )}

          {/* Mobile Menu Toggle */}
          {!hideMenu && (
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden p-2 text-white" data-testid="mobile-menu-toggle">
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          )}
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && !hideMenu && (
        <div className="md:hidden bg-[#0B0F0E] border-t border-white/5">
          <div className="container-custom py-4 space-y-4">
            {menuItems.map((item) => (
              item.isPage ? (
                <Link key={item.label} to={item.href} onClick={() => setIsMenuOpen(false)} className="block py-2 text-[#E5E7EB] hover:text-[#10b981]">
                  {item.label}
                </Link>
              ) : (
                <button key={item.label} onClick={() => scrollToSection(item.href)} className="block w-full text-left py-2 text-[#E5E7EB] hover:text-[#10b981]">
                  {item.label}
                </button>
              )
            ))}
            <div className="flex gap-4 pt-4">
              {socialMedia.map((social) => (
                <a key={social.id} href={social.url || '#'} target="_blank" rel="noopener noreferrer" className="text-[#9CA3AF] hover:text-[#10b981]">
                  <SocialIcon platform={social.platform} />
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;